import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {DataObjectAssignmentFilterInput, DataObjectAssignmentType, TagType, MaterialOutputType} from "@generated"
import {
    DataObjectDetailsForNameAssetFromSchemaServiceFragment,
    DataObjectDetailsForNameAssetFromSchemaServiceFragment as DataObjectDetails,
    MaterialDetailsForNameAssetFromSchemaServiceFragment as MaterialDetails,
} from "@app/common/services/name-asset-from-schema/name-asset-from-schema.generated"
import {deserializeMaterialGraph, getPhysicalSizeInfoForMaterialGraph} from "@cm/material-nodes"
import {PbrExport} from "@cm/material-maps-exporter"
import {DataObjectDefinitions, MediaType} from "@cm/utils/data-object"
import {IsNonNull} from "@cm/utils/filter"
import {MaterialOutputsFilenames} from "@cm/material-outputs-filenames"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {
    MaterialForNameAssetFromSchemaServiceGQL,
    MaterialsForNameAssetFromSchemaServiceGQL,
    NameAssetFromSchemaUpdateDataObjectGQL,
} from "@common/services/name-asset-from-schema/name-asset-from-schema.generated"
import {FlatOption} from "@labels"
import {DataResolverService} from "@material-editor/services/data-resolver.service"
import {extensionForContentType} from "@cm/utils"
import {TileableMaterialPostRenderStepInput} from "@cm/job-nodes"
import {isPbrExportMaterialOutputType} from "@app/common/helpers/material-maps-exporter"

function getRangeInfoForMaterial(material: MaterialDetails) {
    const materialRangeList = material.tagAssignments
        .filter((tag) => tag.tag.type === TagType.MaterialRange)
        .map((tag) => {
            return {
                name: tag.tag.name,
                otherInfo: tag.tag.otherInfo === undefined ? null : tag.tag.otherInfo,
            }
        })

    if (materialRangeList.length > 1) throw Error(`At most one material range expected for material, found ${materialRangeList.length}`)
    return materialRangeList.length === 1 ? materialRangeList[0] : {name: null, otherInfo: null}
}

function ensureOrganizationNameProvided(material: Pick<MaterialDetails, "organization">) {
    if (!material.organization || !material.organization.name) throw Error(`Organization name missing`)
    return material.organization.name
}

function getRelatedDataObjectForFormatAndResolution(
    dataObject: DataObjectDetails,
    format: MaterialOutputsFilenames.ImageFormat,
    resolution: MaterialOutputsFilenames.ImageResolution,
) {
    if (dataObject.related === undefined) throw Error(`Expected related data objects to be present`)
    const format_ = format === "tif" ? "tiff" : format // derived data objects filenames contain "tiff" instead of "tif"

    return dataObject.related.find((related) => {
        return (
            MaterialOutputsFilenames.ensureValidMediaType(related.mediaType) === format &&
            related.objectName.endsWith(resolution === "original" ? `-${format_}` : `-${format_}-${resolution.slice("px".length)}`)
        )
    })
}

@Injectable({
    providedIn: "root",
})
export class NameAssetFromSchemaService {
    private readonly materialsForNameAssetFromSchemaService = inject(MaterialsForNameAssetFromSchemaServiceGQL)
    private readonly materialForNameAssetFromSchemaService = inject(MaterialForNameAssetFromSchemaServiceGQL)
    private readonly nameAssetFromSchemaUpdateDataObject = inject(NameAssetFromSchemaUpdateDataObjectGQL)
    private readonly dataResolver = inject(DataResolverService)
    private readonly logger: CmLogger = inject(CM_LOGGER_MATERIALS)

    private async fetchMaterialWithDataObjectAssignmentForType(materialId: string, assignmentType?: DataObjectAssignmentType, includeRelated?: boolean) {
        const dataObjectAssignmentFilter: DataObjectAssignmentFilterInput = assignmentType !== undefined ? {assignmentType: [assignmentType]} : {}
        const materials = (
            await fetchThrowingErrors(this.materialsForNameAssetFromSchemaService)({
                filter: {id: {equals: materialId}},
                dataObjectAssignmentFilter: dataObjectAssignmentFilter,
                includeRelated: includeRelated ?? false,
            })
        ).materials.filter(IsNonNull)
        if (materials.length !== 1) throw Error(`Expected exactly one material for id ${materialId}, found ${materials.length}`)

        if (assignmentType !== undefined && materials[0].dataObjectAssignments.length != 1)
            throw Error(`Single assignment for specified type expected, found ${materials[0].dataObjectAssignments.length}`)

        return materials[0]
    }

    private async getPhysicalInfoForMaterial(material: MaterialDetails) {
        if (!material.latestCyclesRevision) throw Error(`Material has no latest cycles revision`)
        const materialGraph = deserializeMaterialGraph(material.latestCyclesRevision.graphNode)
        const info = await getPhysicalSizeInfoForMaterialGraph(materialGraph, this.dataResolver.resolve, this.logger)
        if (!info) throw Error(`Could not determine physical size for material ${material.legacyId}`)
        return {
            widthCm: info.widthCm,
            heightCm: info.heightCm,
        }
    }

    private async getPartialAssetData(material: MaterialDetails) {
        const {widthCm, heightCm} = await this.getPhysicalInfoForMaterial(material)

        return {
            organizationName: ensureOrganizationNameProvided(material).toLowerCase(),
            materialData: {
                articleId: material.articleId ?? null,
                name: material.name ?? null,
                rangeInfo: getRangeInfoForMaterial(material),
                widthCm,
                heightCm,
                revisionNumber: material.latestCyclesRevision?.number ?? null,
            },
        }
    }

    async renameRenderFromSchema(materialId: string, assetType: DataObjectAssignmentType) {
        if (!MaterialOutputsFilenames.isAssignmentTypeValidAssetType(assetType) || !MaterialOutputsFilenames.isRenderAssetType(assetType))
            throw Error(`Invalid asset type: ${assetType}, expected render asset type`)

        const material = await fetchThrowingErrors(this.materialForNameAssetFromSchemaService)({
            materialId,
            dataObjectAssignmentFilter: {assignmentType: [assetType]},
            includeRelated: true,
        }).then(({material}) => material)

        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
            ...partialAssetData,
            type: assetType,
            imageFormat: "exr",
            imageResolution: "original",
        }

        const newFilename = MaterialOutputsFilenames.getFilenameForAssetData(assetData)
        // console.log(`newFilename: ${newFilename}`)
        if (newFilename === null) return

        // filenames for the derived images
        const pending: Promise<DataObjectDetailsForNameAssetFromSchemaServiceFragment>[] = []
        DataObjectDefinitions.DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET.forEach((info) => {
            const derivedDataObjectForInfo = getRelatedDataObjectForFormatAndResolution(
                material.dataObjectAssignments[0].dataObject,
                info.format,
                info.resolution,
            )
            if (derivedDataObjectForInfo === null) throw Error(`Derived data object for format ${info.format} and resolution ${info.resolution} not found`)

            const derivedImageAssetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
                ...assetData,
                imageFormat: info.format,
                imageResolution: info.resolution,
            }

            const derivedImageNewFilename = MaterialOutputsFilenames.getFilenameForAssetData(derivedImageAssetData)
            // console.log(`derived image newFilename ${info.format}/${info.resolution}: ${derivedImageNewFilename}`)
            if (derivedImageNewFilename === null) return

            pending.push(
                mutateThrowingErrors(this.nameAssetFromSchemaUpdateDataObject)({
                    input: {id: derivedDataObjectForInfo!.id, originalFileName: derivedImageNewFilename},
                }).then(({updateDataObject}) => updateDataObject),
            )
        })

        pending.push(
            mutateThrowingErrors(this.nameAssetFromSchemaUpdateDataObject)({
                input: {id: material.dataObjectAssignments[0].dataObject.id, originalFileName: newFilename},
            }).then(({updateDataObject}) => updateDataObject),
        )
        await Promise.all(pending)
    }

    async renameExportFromSchema(materialId: string, exportType: MaterialOutputType) {
        if (!isPbrExportMaterialOutputType(exportType)) throw Error(`Invalid export type: ${exportType}, expected PBR export type`)

        const material = await fetchThrowingErrors(this.materialForNameAssetFromSchemaService)({
            materialId,
            includeRelated: false,
        }).then(({material}) => material)

        const outputForExportType = material.outputs.find((output) => output.type === exportType)
        if (!outputForExportType) throw Error(`Output for export type ${exportType} expected but not found for material ${materialId}`)

        const parsedExportConfig = PbrExport.Config.isExportConfig(outputForExportType.config?.content) ? outputForExportType.config.content : null
        if (parsedExportConfig === null) throw Error(`Invalid export config for material ${materialId} and export type ${exportType}`)

        const outputResultDataObjectId = outputForExportType.result?.id
        if (!outputResultDataObjectId) throw Error(`Output result data object id missing for material ${materialId} and export type ${exportType}`)

        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData = {
            type: DataObjectAssignmentType.MaterialMapsExport,
            ...partialAssetData,
            exportConfig: parsedExportConfig,
        }
        const newExportName = MaterialOutputsFilenames.getFilenameForAssetData(assetData)
        if (newExportName === null) return

        await mutateThrowingErrors(this.nameAssetFromSchemaUpdateDataObject)({
            input: {id: outputResultDataObjectId, originalFileName: `${newExportName}.zip`},
        })
    }

    private defaultFilenameForDerivedImage<T extends (typeof DataObjectDefinitions.DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET)[number]>(
        format: T["format"],
        resolution: T["resolution"],
        nameBase: string,
    ) {
        const suffixResolution = resolution === "original" ? "" : resolution.replace("px", "-")
        const extension = format === "jpg" ? "jpg" : "tiff"
        const suffix = `-${extension}${suffixResolution}`
        return `${nameBase}${suffix}.${extension}`
    }

    private async getRenderAssetNameSetForMaterialAndType(materialId: string, assetType: MaterialOutputsFilenames.RenderAssetType, defaultNameBase: string) {
        const material = await fetchThrowingErrors(this.materialForNameAssetFromSchemaService)({
            materialId,
            dataObjectAssignmentFilter: {assignmentType: [assetType]},
            includeRelated: true,
        }).then(({material}) => material)

        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
            type: assetType,
            ...partialAssetData,
            imageFormat: "exr",
            imageResolution: "original",
        }

        const filename = MaterialOutputsFilenames.getFilenameForAssetData(assetData) ?? `${defaultNameBase}.exr`

        const derivedImagesFilenames = DataObjectDefinitions.DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET.map((info) => {
            const derivedImageAssetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
                ...assetData,
                imageFormat: info.format,
                imageResolution: info.resolution,
            }

            const derivedImageNewFilename =
                MaterialOutputsFilenames.getFilenameForAssetData(derivedImageAssetData) ??
                this.defaultFilenameForDerivedImage(info.format, info.resolution, defaultNameBase)
            // console.log(`derived image newFilename ${info.format}/${info.resolution}: ${derivedImageNewFilename}`)

            return {
                format: info.format,
                resolution: info.resolution,
                filename: derivedImageNewFilename,
            }
        })

        return {
            mainImageFilename: filename,
            derivedImagesFilenames: derivedImagesFilenames as unknown as DataObjectDefinitions.DerivedImageFilenameWithFormatAndResolutionSet,
        }
    }

    async getMaterialTileableRenderNameSet(materialId: string, defaultNameBase: string) {
        return this.getRenderAssetNameSetForMaterialAndType(materialId, DataObjectAssignmentType.MaterialTileableRender, defaultNameBase)
    }

    async getMaterialThumbnailNameSet(materialId: string, assetType: FlatOption["dataObjectAssignmentType"], defaultNameBase: string) {
        if (!MaterialOutputsFilenames.isAssignmentTypeValidAssetType(assetType) || !MaterialOutputsFilenames.isRenderAssetType(assetType))
            throw Error(`Invalid asset type: ${assetType}, expected render asset type`)
        return this.getRenderAssetNameSetForMaterialAndType(materialId, assetType, defaultNameBase)
    }

    async getMaterialMapsExportName(materialId: string, exportConfig: PbrExport.Types.Config) {
        const material = await this.fetchMaterialWithDataObjectAssignmentForType(materialId)
        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData = {
            type: DataObjectAssignmentType.MaterialMapsExport,
            ...partialAssetData,
            exportConfig,
        }
        return MaterialOutputsFilenames.getFilenameForAssetData(assetData)
    }

    async getMaterialMapsExportMapName(materialId: string, conversionConfig: PbrExport.Types.ConversionRequest, mapType: MaterialOutputsFilenames.MapType) {
        const material = await this.fetchMaterialWithDataObjectAssignmentForType(materialId)
        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData = {
            type: MaterialOutputsFilenames.MAPS_EXPORT_MAP_ASSET_TYPE,
            ...partialAssetData,
            conversionConfig,
            mapType,
        }
        return MaterialOutputsFilenames.getFilenameForAssetData(assetData)
    }

    async getMaterialMapsExportInfoName(materialId: string, conversionInfoRequest: PbrExport.Types.ConversionInfoRequest) {
        const material = await this.fetchMaterialWithDataObjectAssignmentForType(materialId)
        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData = {
            type: MaterialOutputsFilenames.MAPS_EXPORT_INFO_ASSET_TYPE,
            ...partialAssetData,
            conversionInfo: conversionInfoRequest,
        }
        return MaterialOutputsFilenames.getFilenameForAssetData(assetData)
    }

    async getMaterialFabricOverviewNameSet(
        _materialId: string,
        defaultNameBase: string,
        mediaType: MediaType,
    ): Promise<TileableMaterialPostRenderStepInput["filenames"]> {
        const mainImageFilename = `${defaultNameBase}.${extensionForContentType(mediaType)}`
        return {
            mainImageFilename,
            derivedImagesFilenames: null,
        }
    }
}
